'use client';

import { useTranslations } from 'next-intl';
import gsap from 'gsap';
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from '../../../hooks/animation/useGsapContext';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { CustomImage as Image } from 'components/shared';
import { BentoHeading, BentoPara } from './StyleConstants';
import styles from './NudgeCard.module.css';

export const NudgeCard = ({
  className,
  title,
  description,
  image,
}: {
  className?: string;
  title?: string;
  description?: string;
  image?: string;
}) => {
  const t = useTranslations('ring');
  const tHome = useTranslations('home');
  const container = useRef<HTMLDivElement>(null);
  const gsapContext = useGsapContext();
  const { add } = useMasterTimeline();

  const animationInit = () => {
    if (!container.current) return;

    gsapContext.add(() => {
      const tl = gsap.timeline({
        defaults: {
          duration: 1,
        },
        scrollTrigger: {
          trigger: container.current,
          scrub: 0.5,
          start: 'top 60%',
          end: 'bottom 30%',
        },
      });

      tl.fromTo(
        '.translateup',
        {
          yPercent: 0,
        },
        {
          yPercent: -40,
        },
      );

      add(tl, 'bento-nudge-card', 4);

      tl.pause();
    });
  };

  useIsomorphicLayoutEffect(() => {
    const animTimer = setTimeout(animationInit, 100);

    return () => {
      if (animTimer) clearTimeout(animTimer);
    };
  }, []);

  return (
    <div
      className={`${styles.root} container${className ? ` ${className}` : ''}`}
      ref={container}
    >
      <div className={styles.text}>
        <BentoHeading>
          {title ?? t('ring.bentoSection.nudgeCard.head')}
        </BentoHeading>
        <BentoPara>
          {description ?? t('ring.bentoSection.nudgeCard.desc')}
        </BentoPara>
      </div>
      <div className={styles.image}>
        <Image
          className="translateup"
          src={image ?? 'web_v2/illustrations/nudges-trail.png'}
          alt={tHome('home.bentoCards.nudge.alt')}
          fill
        />
      </div>
    </div>
  );
};
